import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { format, parseISO, startOfDay, addHours } from "date-fns";
import { toZonedTime, fromZonedTime, getTimezoneOffset } from "date-fns-tz";

const API = "https://erp-xe9u.onrender.com/api";

export default function AddTime() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [entries, setEntries] = useState([createEmptyEntry()]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState({});

  const userTimezone =
    localStorage.getItem("userTimezone") ||
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    "UTC";

  const navigate = useNavigate();

  // Create empty entry template
  function createEmptyEntry() {
    return {
      id: Date.now() + Math.random(),
      date: format(new Date(), "yyyy-MM-dd"),
      projectId: "",
      workOrderId: "",
      startTime: "",
      endTime: "",
      breakDuration: 0,
      notes: "",
      usePersonalVehicle: false,
      status: "draft", // draft, submitting, submitted, error
    };
  }

  // Get work orders for a specific project
  const getWorkOrdersForProject = (projectId) => {
    const project = projects.find((p) => p._id === projectId);
    if (!project) return [];

    // V3 projects only populate workOrders (no workOrders2)
    if (project.version === 3) {
      return (project.workOrders || []).map((wo) => ({
        ...wo,
        source: "workOrders3",
      }));
    }

    return [
      ...(project.workOrders || []).map((wo) => ({
        ...wo,
        source: "workOrders",
      })),
      ...(project.workOrders2 || []).map((wo) => ({
        ...wo,
        source: "workOrders2",
      })),
    ];
  };

  // Format date for datetime-local input
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    try {
      const date = parseISO(dateString);
      const zonedDate = toZonedTime(date, userTimezone);
      return format(zonedDate, "yyyy-MM-dd'T'HH:mm");
    } catch (error) {
      return "";
    }
  };

  // Format time only for display
  const formatTimeOnly = (dateString) => {
    if (!dateString) return "";
    try {
      const date = parseISO(dateString);
      const zonedDate = toZonedTime(date, userTimezone);
      return format(zonedDate, "HH:mm");
    } catch (error) {
      return "";
    }
  };

  // Convert local time input to UTC ISO string
  const localToUTC = (value) => {
    if (!value) return "";
    try {
      const localDate = new Date(value);
      const utcDate = fromZonedTime(localDate, userTimezone);
      return isNaN(utcDate.getTime()) ? "" : utcDate.toISOString();
    } catch (error) {
      return "";
    }
  };

  // Get timezone offset in hours
  const getTimezoneOffsetHours = () => {
    try {
      const offsetMs = getTimezoneOffset(userTimezone, new Date());
      return offsetMs / 1000 / 60 / 60;
    } catch (error) {
      return 0;
    }
  };

  // Calculate duration in hours
  const calculateDuration = (start, end, breakMins) => {
    if (!start || !end) return null;
    try {
      const startDate = new Date(start);
      const endDate = new Date(end);
      const diffMs = endDate - startDate - (breakMins || 0) * 60 * 1000;
      return Math.max(0, diffMs / 1000 / 60 / 60);
    } catch {
      return null;
    }
  };

  // Format duration for display
  const formatDuration = (hours) => {
    if (hours === null) return "--";
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API}/users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUsers(response.data);
      } catch (error) {
        setErrorMessage("Failed to load users: " + error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  // Fetch projects when user is selected
  useEffect(() => {
    const fetchProjects = async () => {
      if (!selectedUser) {
        setProjects([]);
        return;
      }
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API}/mobile/projects/hourlyV2`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error("Failed to fetch projects");
        const data = await response.json();
        setProjects(
          data.map((project) => ({
            _id: project._id,
            projectName: project.projectName,
            version: project.version,
            workOrders: (project.workOrders || []).filter(
              (wo) => wo.type === "hourly" || wo.type === "union_hourly",
            ),
            workOrders2: (project.workOrders2 || []).filter(
              (wo) => wo.type === "hourly" || wo.type === "union_hourly",
            ),
            address: project.address,
          })),
        );
      } catch (error) {
        setErrorMessage("Failed to load projects: " + error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, [selectedUser]);

  // Update entry field
  const updateEntry = (id, field, value) => {
    setEntries((prev) =>
      prev.map((entry) => {
        if (entry.id !== id) return entry;

        const updated = { ...entry, [field]: value };

        // Reset work order if project changes
        if (field === "projectId") {
          updated.workOrderId = "";
        }

        return updated;
      }),
    );
  };

  // Handle time input change
  const handleTimeChange = (id, field, timeValue) => {
    if (!timeValue) {
      updateEntry(id, field, "");
      return;
    }
    // Get the entry's date
    const entry = entries.find((e) => e.id === id);
    if (!entry) return;

    // Combine entry's date with time
    const dateTimeString = `${entry.date}T${timeValue}`;
    const utcString = localToUTC(dateTimeString);
    updateEntry(id, field, utcString);
  };

  // Handle date change for an entry
  const handleDateChange = (id, newDate) => {
    setEntries((prev) =>
      prev.map((entry) => {
        if (entry.id !== id) return entry;
        // Reset times when date changes
        return {
          ...entry,
          date: newDate,
          startTime: "",
          endTime: "",
        };
      }),
    );
  };

  // Add new entry
  const addEntry = () => {
    setEntries((prev) => [...prev, createEmptyEntry()]);
  };

  // Remove entry
  const removeEntry = (id) => {
    if (entries.length === 1) return;
    setEntries((prev) => prev.filter((e) => e.id !== id));
  };

  // Duplicate entry
  const duplicateEntry = (entry) => {
    const newEntry = {
      ...entry,
      id: Date.now() + Math.random(),
      status: "draft",
      startTime: "",
      endTime: "",
      notes: "",
    };
    setEntries((prev) => [...prev, newEntry]);
  };

  // Validate single entry
  const validateEntry = (entry) => {
    if (!entry.projectId) return "Project is required";
    if (!entry.workOrderId) return "Work order is required";
    if (!entry.startTime) return "Start time is required";
    if (!entry.endTime) return "End time is required";
    if (new Date(entry.startTime) >= new Date(entry.endTime)) {
      return "End time must be after start time";
    }
    return null;
  };

  // Submit single entry
  const submitEntry = async (entry) => {
    const validationError = validateEntry(entry);
    if (validationError) {
      setErrorMessage(validationError);
      return false;
    }

    // Find the selected work order (now from combined list)
    const workOrder = getWorkOrdersForProject(entry.projectId).find(
      (wo) => wo._id === entry.workOrderId,
    );

    if (!workOrder) {
      setErrorMessage("Selected work order not found");
      return false;
    }

    const payload = {
      projectId: entry.projectId,
      workOrderId: entry.workOrderId, // ← important! send the _id
      start: entry.startTime,
      end: entry.endTime,
      email: selectedUser.email,
      userId: selectedUser._id, // ← add this!
      usePersonalVehicle: entry.usePersonalVehicle,
      kilometers: 0,
      breaks:
        entry.breakDuration > 0 ? [{ duration: entry.breakDuration }] : [],
      locations: [
        { latitude: "0", longitude: "0", timestamp: new Date().toISOString() },
      ],
      timezone: userTimezone,
      timezoneOffset: getTimezoneOffsetHours(),
      notes: entry.notes,
    };

    setSubmitting((prev) => ({ ...prev, [entry.id]: true }));
    updateEntry(entry.id, "status", "submitting");

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/timesheets/clock-in-out-offline`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      updateEntry(entry.id, "status", "submitted");
      return true;
    } catch (error) {
      console.error(error);
      updateEntry(entry.id, "status", "error");
      setErrorMessage(
        `Failed to submit: ${error.response?.data?.message || error.message}`,
      );
      return false;
    } finally {
      setSubmitting((prev) => ({ ...prev, [entry.id]: false }));
    }
  };

  // Submit all draft entries
  const submitAll = async () => {
    const draftEntries = entries.filter((e) => e.status === "draft");
    if (draftEntries.length === 0) {
      setErrorMessage("No entries to submit");
      return;
    }

    // Validate all first
    for (const entry of draftEntries) {
      const error = validateEntry(entry);
      if (error) {
        setErrorMessage(`Entry validation failed: ${error}`);
        return;
      }
    }

    let successCount = 0;
    for (const entry of draftEntries) {
      const success = await submitEntry(entry);
      if (success) successCount++;
    }

    if (successCount === draftEntries.length) {
      setSuccessMessage(`Successfully submitted ${successCount} time entries`);
      setTimeout(() => navigate("/timesheets"), 2000);
    } else {
      setErrorMessage(
        `Submitted ${successCount} of ${draftEntries.length} entries. Some entries failed.`,
      );
    }
  };

  // Calculate totals
  const totals = useMemo(() => {
    let totalHours = 0;
    let totalBreakMins = 0;
    let validEntries = 0;

    entries.forEach((entry) => {
      const duration = calculateDuration(
        entry.startTime,
        entry.endTime,
        entry.breakDuration,
      );
      if (duration !== null) {
        totalHours += duration;
        validEntries++;
      }
      totalBreakMins += entry.breakDuration || 0;
    });

    return { totalHours, totalBreakMins, validEntries };
  }, [entries]);

  const draftCount = entries.filter((e) => e.status === "draft").length;
  const submittedCount = entries.filter((e) => e.status === "submitted").length;

  return (
    <div className="min-h-screen bg-gray-900 p-6 rounded-lg m-4">
      <div className="max-w-6xl mx-auto space-y-6 ">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white">Add Time Entries</h1>
            <p className="text-gray-400 text-sm mt-1">
              Add multiple time entries for an employee's day
            </p>
          </div>
          <button
            onClick={() => navigate("/timesheets")}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 font-medium rounded-lg transition-colors"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Timesheets
          </button>
        </div>

        {/* Messages */}
        {errorMessage && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex justify-between items-center">
            <span className="text-red-400">{errorMessage}</span>
            <button
              onClick={() => setErrorMessage(null)}
              className="text-red-400 hover:text-red-300"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        {successMessage && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 flex justify-between items-center">
            <span className="text-emerald-400">{successMessage}</span>
            <button
              onClick={() => setSuccessMessage(null)}
              className="text-emerald-400 hover:text-emerald-300"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Employee Selection */}
        <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
          <div>
            <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
              Employee
            </label>
            <select
              value={selectedUser?._id || ""}
              onChange={(e) => {
                const user = users.find((u) => u._id === e.target.value);
                setSelectedUser(user || null);
                setEntries([createEmptyEntry()]);
              }}
              className="w-full md:w-96 bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-gray-200 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
              disabled={loading}
            >
              <option value="">Select Employee</option>
              {users.map((user) => (
                <option key={user._id} value={user._id}>
                  {user.displayName || user.email}
                </option>
              ))}
            </select>
          </div>

          {selectedUser && (
            <div className="mt-4 pt-4 border-t border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center">
                  <span className="text-orange-400 font-medium">
                    {(selectedUser.displayName || selectedUser.email)
                      .charAt(0)
                      .toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-white font-medium">
                    {selectedUser.displayName || "No name"}
                  </p>
                  <p className="text-gray-500 text-sm">{selectedUser.email}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500 uppercase tracking-wider">
                  Timezone
                </p>
                <p className="text-gray-300 text-sm">{userTimezone}</p>
              </div>
            </div>
          )}
        </div>

        {/* Time Entries */}
        {selectedUser && (
          <>
            {/* Summary Bar */}
            <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-6">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">
                      Entries
                    </p>
                    <p className="text-xl font-bold text-white">
                      {entries.length}
                    </p>
                  </div>
                  <div className="w-px h-8 bg-gray-700"></div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">
                      Total Hours
                    </p>
                    <p className="text-xl font-bold text-white">
                      {formatDuration(totals.totalHours)}
                    </p>
                  </div>
                  <div className="w-px h-8 bg-gray-700"></div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">
                      Total Breaks
                    </p>
                    <p className="text-xl font-bold text-white">
                      {totals.totalBreakMins}m
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {submittedCount > 0 && (
                    <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded border border-emerald-500/30">
                      {submittedCount} submitted
                    </span>
                  )}
                  {draftCount > 0 && (
                    <span className="text-xs bg-gray-600 text-gray-300 px-2 py-1 rounded">
                      {draftCount} pending
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Entries List */}
            <div className="space-y-4">
              {entries.map((entry, index) => {
                const workOrders = getWorkOrdersForProject(entry.projectId);
                const duration = calculateDuration(
                  entry.startTime,
                  entry.endTime,
                  entry.breakDuration,
                );
                const isSubmitting = submitting[entry.id];
                const isSubmitted = entry.status === "submitted";
                const hasError = entry.status === "error";

                return (
                  <div
                    key={entry.id}
                    className={`bg-gray-800 rounded-xl border transition-all ${
                      isSubmitted
                        ? "border-emerald-500/30 bg-emerald-500/5"
                        : hasError
                          ? "border-red-500/30 bg-red-500/5"
                          : "border-gray-700"
                    }`}
                  >
                    {/* Entry Header */}
                    <div className="px-5 py-3 border-b border-gray-700/50 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="w-7 h-7 bg-gray-700 rounded-full flex items-center justify-center text-sm text-gray-400 font-medium">
                          {index + 1}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400 text-sm">
                            {format(parseISO(entry.date), "MMM d, yyyy")}
                          </span>
                          <span className="text-gray-600">•</span>
                          <span className="text-white font-medium">
                            {entry.projectId
                              ? projects.find((p) => p._id === entry.projectId)
                                  ?.projectName || "Entry"
                              : "New Entry"}
                          </span>
                        </div>
                        {isSubmitted && (
                          <span className="flex items-center gap-1 text-xs text-emerald-400">
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                            Submitted
                          </span>
                        )}
                        {hasError && (
                          <span className="flex items-center gap-1 text-xs text-red-400">
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            Failed
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {duration !== null && (
                          <span className="text-gray-400 font-mono text-sm mr-2">
                            {formatDuration(duration)}
                          </span>
                        )}
                        {!isSubmitted && (
                          <>
                            <button
                              onClick={() => duplicateEntry(entry)}
                              className="p-2 text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded-lg transition-colors"
                              title="Duplicate entry"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                />
                              </svg>
                            </button>
                            {entries.length > 1 && (
                              <button
                                onClick={() => removeEntry(entry.id)}
                                className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                title="Remove entry"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Entry Form */}
                    <div className="p-5">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                        {/* Date */}
                        <div>
                          <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5">
                            Date
                          </label>
                          <input
                            type="date"
                            value={entry.date}
                            onChange={(e) =>
                              handleDateChange(entry.id, e.target.value)
                            }
                            disabled={isSubmitted || isSubmitting}
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2.5 text-gray-200 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </div>

                        {/* Project */}
                        <div>
                          <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5">
                            Project
                          </label>
                          <select
                            value={entry.projectId}
                            onChange={(e) =>
                              updateEntry(entry.id, "projectId", e.target.value)
                            }
                            disabled={isSubmitted || isSubmitting}
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2.5 text-gray-200 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <option value="">Select Project</option>
                            {projects.map((project) => (
                              <option key={project._id} value={project._id}>
                                {project.projectName}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Work Order */}
                        <div>
                          <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5">
                            Work Order
                          </label>
                          <select
                            value={entry.workOrderId}
                            onChange={(e) =>
                              updateEntry(
                                entry.id,
                                "workOrderId",
                                e.target.value,
                              )
                            }
                            disabled={
                              !entry.projectId || isSubmitted || isSubmitting
                            }
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2.5 text-gray-200 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <option value="">Select Work Order</option>
                            {workOrders.map((wo) => (
                              <option key={wo._id} value={wo._id}>
                                {wo.woNumber} - {wo.title}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Start Time */}
                        <div>
                          <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5">
                            Start Time
                          </label>
                          <input
                            type="time"
                            value={formatTimeOnly(entry.startTime)}
                            onChange={(e) =>
                              handleTimeChange(
                                entry.id,
                                "startTime",
                                e.target.value,
                              )
                            }
                            disabled={isSubmitted || isSubmitting}
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2.5 text-gray-200 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </div>

                        {/* End Time */}
                        <div>
                          <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5">
                            End Time
                          </label>
                          <input
                            type="time"
                            value={formatTimeOnly(entry.endTime)}
                            onChange={(e) =>
                              handleTimeChange(
                                entry.id,
                                "endTime",
                                e.target.value,
                              )
                            }
                            disabled={isSubmitted || isSubmitting}
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2.5 text-gray-200 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </div>
                      </div>

                      {/* Second Row */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                        {/* Break Duration */}
                        <div>
                          <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5">
                            Break (minutes)
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="5"
                            value={entry.breakDuration || ""}
                            onChange={(e) =>
                              updateEntry(
                                entry.id,
                                "breakDuration",
                                parseInt(e.target.value) || 0,
                              )
                            }
                            disabled={isSubmitted || isSubmitting}
                            placeholder="0"
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2.5 text-gray-200 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </div>

                        {/* Notes */}
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5">
                            Notes
                          </label>
                          <input
                            type="text"
                            value={entry.notes}
                            onChange={(e) =>
                              updateEntry(entry.id, "notes", e.target.value)
                            }
                            disabled={isSubmitted || isSubmitting}
                            placeholder="Optional notes..."
                            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2.5 text-gray-200 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </div>
                      </div>

                      {/* Entry Actions */}
                      {!isSubmitted && (
                        <div className="mt-4 pt-4 border-t border-gray-700/50 flex items-center justify-between">
                          <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={entry.usePersonalVehicle}
                              onChange={(e) =>
                                updateEntry(
                                  entry.id,
                                  "usePersonalVehicle",
                                  e.target.checked,
                                )
                              }
                              disabled={isSubmitting}
                              className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-orange-500 focus:ring-orange-500 focus:ring-offset-0"
                            />
                            Personal vehicle used
                          </label>
                          <button
                            onClick={() => submitEntry(entry)}
                            disabled={
                              isSubmitting ||
                              !entry.projectId ||
                              !entry.workOrderId ||
                              !entry.startTime ||
                              !entry.endTime
                            }
                            className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                          >
                            {isSubmitting ? (
                              <>
                                <svg
                                  className="w-4 h-4 animate-spin"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  ></path>
                                </svg>
                                Submitting...
                              </>
                            ) : (
                              <>
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                                Submit Entry
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Add Entry & Submit All */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
              <button
                onClick={addEntry}
                className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-600 hover:border-gray-500 text-gray-400 hover:text-gray-200 rounded-xl transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add Another Entry
              </button>

              {draftCount > 0 && (
                <button
                  onClick={submitAll}
                  disabled={Object.values(submitting).some(Boolean)}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Submit All ({draftCount}) Entries
                </button>
              )}
            </div>
          </>
        )}

        {/* Empty State */}
        {!selectedUser && !loading && (
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-12 text-center">
            <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
            <h3 className="text-white font-medium mb-1">Select an Employee</h3>
            <p className="text-gray-500 text-sm">
              Choose an employee above to start adding time entries
            </p>
          </div>
        )}

        {loading && (
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-12 text-center">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-400">Loading...</p>
          </div>
        )}
      </div>
    </div>
  );
}
